import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { escapeLegalSlides, type EscapeBlock, type Role } from '@il-colpo/engine';
import { useSettings } from '../../state/settingsStore.ts';
import { Button } from '../../ui/index.ts';
import { clock } from '../format.ts';
import { useGame } from '../GameContext.tsx';
import { HeardList, type PuzzleProps } from './shared.tsx';
import styles from './puzzles.module.css';
import { CELL, VehicleArt } from './Vehicle.tsx';

interface EscapeView {
  size: number;
  carRow: number;
  controller: Role;
  blocks: EscapeBlock[];
  moves: number;
  radio?: { atMs: number; side: string } | null;
}

const HEARD = ['escReinforce', 'escMove'] as const;

/** Escape (GDD §8.8): sliding blocks. Everybody watches; only the driver's hands move them. */
export function EscapePanel({ puzzleId, v: raw }: PuzzleProps) {
  const { t } = useTranslation('game');
  const { role, act, view, g } = useGame();
  const v = raw as unknown as EscapeView;
  const [picked, setPicked] = useState<number | null>(null);
  const mine = role === v.controller;
  const carStyle = useSettings().game.escapeCar;
  const block = v.blocks.find((b) => b.id === picked);
  const legal = useMemo(
    () => (block ? escapeLegalSlides(v.blocks, block.id) : []),
    [block, v.blocks],
  );

  const slide = (delta: number) => {
    if (block && legal.includes(delta))
      act(puzzleId, { kind: 'escape.move', block: block.id, delta });
  };

  useEffect(() => {
    if (!mine || !block) return;
    const onKey = (e: KeyboardEvent) => {
      const forward = block.horiz ? 'ArrowRight' : 'ArrowDown';
      const back = block.horiz ? 'ArrowLeft' : 'ArrowUp';
      if (e.key === forward || e.key === back) {
        e.preventDefault();
        const delta = e.key === forward ? 1 : -1;
        if (legal.includes(delta)) act(puzzleId, { kind: 'escape.move', block: block.id, delta });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mine, block, legal, act, puzzleId]);

  return (
    <div className={styles['stack']}>
      <p className={styles['muted']}>
        {mine
          ? t('puzzle.escape.yours')
          : t('puzzle.escape.watch', { who: g(`roles.${v.controller}`) })}
      </p>
      <p className={styles['progress']}>{t('puzzle.escape.moves', { n: v.moves })}</p>
      <svg
        viewBox={`0 0 ${v.size * CELL} ${v.size * CELL}`}
        className={styles['board']}
        role="group"
        aria-label={t('puzzle.escape.board')}
      >
        {Array.from({ length: v.size * v.size }, (_, i) => (
          <rect
            key={i}
            className={styles['cell']}
            x={(i % v.size) * CELL}
            y={Math.floor(i / v.size) * CELL}
            width={CELL}
            height={CELL}
          />
        ))}
        <rect
          className={styles['gate']}
          x={v.size * CELL - 6}
          y={v.carRow * CELL + 4}
          width={6}
          height={CELL - 8}
        />
        {v.blocks.map((b) => {
          const w = (b.horiz ? b.len : 1) * CELL;
          const h = (b.horiz ? 1 : b.len) * CELL;
          const car = b.id === 0;
          return (
            <g
              key={b.id}
              role={mine ? 'button' : 'img'}
              tabIndex={mine ? 0 : undefined}
              aria-label={
                car
                  ? t('puzzle.escape.car')
                  : b.police
                    ? t('puzzle.escape.police', { n: b.id })
                    : t('puzzle.escape.block', { n: b.id })
              }
              aria-pressed={mine ? picked === b.id : undefined}
              className={[
                styles['block'],
                car ? styles['car'] : '',
                b.police ? styles['police'] : '',
                picked === b.id ? styles['selected'] : '',
              ].join(' ')}
              data-style={car ? carStyle : undefined}
              transform={`translate(${b.x * CELL},${b.y * CELL})`}
              onClick={mine ? () => setPicked(b.id) : undefined}
              onKeyDown={
                mine
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setPicked(b.id);
                      }
                    }
                  : undefined
              }
            >
              <rect x={3} y={3} width={w - 6} height={h - 6} rx={8} />
              <VehicleArt
                len={b.len}
                horiz={b.horiz}
                kind={car ? 'car' : b.police ? 'police' : b.len >= 3 ? 'truck' : 'van'}
                style={carStyle}
              />
              <text x={w / 2} y={h / 2 + 5} textAnchor="middle">
                {car ? '★' : b.police ? 'P' : b.id}
              </text>
            </g>
          );
        })}
      </svg>
      {mine && block ? (
        <div className={styles['slide']} role="group" aria-label={t('puzzle.escape.slide')}>
          <Button
            size="md"
            variant="secondary"
            disabled={!legal.includes(-1)}
            onClick={() => slide(-1)}
          >
            {block.horiz ? '←' : '↑'}
          </Button>
          <Button
            size="md"
            variant="secondary"
            disabled={!legal.includes(1)}
            onClick={() => slide(1)}
          >
            {block.horiz ? '→' : '↓'}
          </Button>
        </div>
      ) : null}
      {mine && v.radio ? (
        <p className={styles['status']} role="status">
          {t('puzzle.escape.radio', {
            side: g(`escapeSides.${v.radio.side}`),
            when: clock(Math.max(0, v.radio.atMs - (view.exec?.elapsedMs ?? 0))),
          })}
        </p>
      ) : null}
      <HeardList puzzleId={puzzleId} ids={HEARD} />
    </div>
  );
}
